import React, { useState } from 'react'
import { Download, ChevronDown, ChevronRight, FileText } from 'lucide-react'
import Button from './Button'
import PaginationFooter from './PaginationFooter'
import { downloadCSV } from '../../utils/helpers'
import DocumentItem from './DocumentItem'

/**
 * Responsive data table component for displaying investment data
 * Supports sorting, pagination, mobile-friendly layouts, and data export
 * Automatically adapts to different screen sizes
 */
const DataTable = ({
  title,
  subtitle,
  columns,
  data,
  pageSize = 5,
  className = '',
  onRowClick,
  enableExport = false,
  exportFilename = 'data-export',
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [expandedRows, setExpandedRows] = useState(new Set())

  // Calculate pagination
  const totalPages = Math.ceil(data.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const currentData = data.slice(startIndex, endIndex)

  // Handle sorting
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  // Sort data
  const sortedData = [...currentData].sort((a, b) => {
    if (!sortColumn) return 0
    
    const aValue = a[sortColumn]
    const bValue = b[sortColumn]
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Handle row click
  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index)
    }
  }

  // Handle CSV export
  const handleCSVExport = () => {
    downloadCSV(data, columns, exportFilename)
  }

  // Toggle document row expansion
  const toggleDocumentExpansion = (rowIndex, event) => {
    event.stopPropagation() // Prevent row click when clicking documents button
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex)
      } else {
        newSet.add(rowIndex)
      }
      return newSet
    })
  }

  return (
    <>
      {/* Export buttons - only show on desktop and when export is enabled */}
      {enableExport && (
        <div className="hidden md:flex justify-end mb-4 space-x-2">
          <Button
            variant="outline"
            size="small"
            onClick={handleCSVExport}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      )}
      
      <div className="overflow-x-auto">
        {/* Desktop Table */}
        <table className="hidden md:table w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 ${
                    sortColumn === column.key ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortColumn === column.key && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((row, index) => {
              const isExpanded = expandedRows.has(index)
              const hasDocuments = row.documents && row.documents.length > 0
              
              return (
                <React.Fragment key={index}>
                  {/* Main data row */}
                  <tr
                    className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => handleRowClick(row, index)}
                  >
                    {columns.map((column) => (
                      column.key !== 'documents' ? (
                        <td
                          key={column.key}
                          className="px-4 py-4 whitespace-nowrap text-sm text-gray-900"
                        >
                          {column.render ? column.render(row[column.key], row) : row[column.key]}
                        </td>
                      ) : (
                        <td
                          key={column.key}
                          className="px-4 py-4 whitespace-nowrap text-sm text-gray-900"
                        >
                          {hasDocuments ? (
                            <Button
                              variant="outline"
                              size="small"
                              onClick={(e) => toggleDocumentExpansion(index, e)}
                              className="flex items-center gap-2 transition-all duration-300 ease-in-out"
                            >
                              <div className={`transition-transform duration-300 ease-in-out ${
                                isExpanded ? 'rotate-0' : '-rotate-90'
                              }`}>
                                <ChevronDown className="w-4 h-4" />
                              </div>
                              <FileText className="w-4 h-4" />
                              {row.documents.length} Document{row.documents.length !== 1 ? 's' : ''}
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">No documents</span>
                          )}
                        </td>
                      )
                    ))}
                  </tr>
                  
                  {/* Expandable documents row - only render when expanded */}
                  {hasDocuments && isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan={columns.length} className="px-4 py-4">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-4 h-4 text-gray-600" />
                            <h4 className="font-medium text-gray-900 text-sm">
                              Documents ({row.documents.length})
                            </h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {row.documents.map((document) => (
                              <DocumentItem 
                                key={document.id} 
                                document={document}
                                className="bg-white"
                              />
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>

        {/* Mobile Cards */}
        {/* <div className="md:hidden space-y-3">
          {sortedData.map((row, index) => (
            <div
              key={index}
              className={`bg-white border border-gray-200 rounded-lg p-4 ${
                onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={() => handleRowClick(row, index)}
            >
              {columns.map((column) => (
                <div key={column.key} className="flex justify-between items-center py-2">
                  <span className="text-sm font-medium text-gray-500">
                    {column.label}:
                  </span>
                  <span className="text-sm text-gray-900">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div> */}
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <PaginationFooter
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageInfoText={`Showing ${startIndex + 1} to ${Math.min(endIndex, data.length)} of ${data.length} results`}
        />
      )}
    </>
  )
}

export default DataTable
